import { INVALID_FEATURE } from 'enums';
import { ValidationError } from 'interfaces/setting.interface';
import { HABIT_ROW_DATA_ATTR } from 'constants/general';
import { ATTRIB } from 'constants/test';

export const getHabitRowDataProps = (
  activityId?: string | null
): Record<string, string> =>
  activityId ? { [HABIT_ROW_DATA_ATTR]: activityId } : {};

export const getHabitRowSelector = (activityId: string): string =>
  `[${HABIT_ROW_DATA_ATTR}="${CSS.escape(activityId)}"]`;

const TIMING_FEATURE_TEST_IDS: Partial<Record<INVALID_FEATURE, string>> = {
  [INVALID_FEATURE.START_UP]: ATTRIB.DATA_TESTID.STARTUP_TIME_WRAPPER,
  [INVALID_FEATURE.SHUTDOWN]: ATTRIB.DATA_TESTID.SHUTDOWN_TIME_WRAPPER,
  [INVALID_FEATURE.SLEEP_TIME]: ATTRIB.DATA_TESTID.CUTOFF_TIME_WRAPPER,
  [INVALID_FEATURE.BREAK_FREQUENCY]: ATTRIB.DATA_TESTID.BREAK_FREQUENCY_WRAPPER
};

export const getTimingFeatureTestId = (
  feature?: INVALID_FEATURE
): string | undefined =>
  feature ? TIMING_FEATURE_TEST_IDS[feature] : undefined;

export const getValidationErrorTabIndex = (
  error: ValidationError,
  routineTabs: string[]
): number => routineTabs.indexOf(error.tab.title);

const focusFirstInteractiveElement = (container: Element): void => {
  const input = container.querySelector(
    'textarea, input, button, [role="spinbutton"]'
  );
  if (input instanceof HTMLElement) {
    input.focus();
  }
};

export const scrollToHabitValidationError = (activityId: string): void => {
  const row = document.querySelector(getHabitRowSelector(activityId));
  row?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if (row) {
    focusFirstInteractiveElement(row);
  }
};

export const scrollToTimingValidationError = (
  feature?: INVALID_FEATURE
): void => {
  const testId = getTimingFeatureTestId(feature);
  if (!testId) {
    return;
  }

  const field = document.querySelector(
    `[data-testid="${CSS.escape(testId)}"]`
  );
  field?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if (field) {
    focusFirstInteractiveElement(field);
  }
};

export const canNavigateToValidationError = (error: ValidationError): boolean => {
  if (error.activityId) {
    return true;
  }

  // Index-based errors (e.g. custom routine schedules) reuse timing feature enums
  // but should not scroll to Timing tab field anchors.
  if (error.position !== undefined) {
    return false;
  }

  return Boolean(getTimingFeatureTestId(error.feature));
};
